import { DetailedSafeObject, DetailedSafeObjectUtils } from "./IDetailedSafeObjecet";
import {
  GROUP_CLS,
  POLICY_CLS,
  PROFILE_CLS,
  TRANSACTION_CLS,
  USER_CLS,
  conditionsToParse,
} from "../../../utils/constants";
import { DetailedGroup, DetailedGroupUtils } from "./DetailedGroup";
import { DetailedPolicy, DetailedPolicyUtils } from "./DetailedPolicy";
import { DetailedUser, DetailedUserUtils } from "./DetailedUser";
import { DetailedTransaction, DetailedTransactionUtils } from "./DetailedTransaction";
import { ProcessUtils } from "../Process";
import { ApplyFunctionUtils } from "../ApplyFunction";
import { ConditionUtils } from "../Condition";
import { ApplyOn, ApplyOnUtils } from "../ApplyOn";
import { BasicProcessPhase, PolicyType } from "../../../utils/enums";
import { TransactionMessageUtils } from "../cmds/Transaction";
import { CurrentPhaseUtils } from "../CurrentPhase";
import { LinkUtils } from "../Link";
import { ParamUtils } from "../Param";

// Placeholder for shouldIgnorePolicy (from utils.dart, assumed)
function shouldIgnorePolicy(applyOn: ApplyOn[]): boolean {
  return false; // Replace with actual implementation
}

// Placeholder for analyzePolicyType (assumed from your utils or elsewhere)
export function analyzePolicyType(applyOn: ApplyOn[]): PolicyType {
  const isGroupPolicy = applyOn.some(
    (c) => c.key === "class" && [GROUP_CLS, POLICY_CLS, USER_CLS].includes(c.value)
  );
  const isTransactionPolicy = applyOn.some(
    (c) => c.key === "class" && [TRANSACTION_CLS].includes(c.value)
  );
  const isRecovery = applyOn.some((c) => c.key === "id" && c.value.includes("noActive"));
  return isGroupPolicy
    ? PolicyType.Groups
    : isTransactionPolicy
      ? PolicyType.Transaction
      : isRecovery
        ? PolicyType.Recovery
        : PolicyType.Error;
}

export const DSOFactory = {
  // Placeholder for the factory methods that will create instances of DetailedSafeObject

  // from various types of maps or data structures.
  fromMap: (map: Record<string, any>): DetailedSafeObject => {
    // This method will determine the type of object based on the map and create an instance accordingly.
    // For now, it returns a new DetailedSafeObject instance with the provided map.
    switch (map["class"]) {
      case GROUP_CLS:
        return DSOFactory.groupFromMap(map);
      case POLICY_CLS:
        return DSOFactory.policyFromMap(map);
      case USER_CLS:
        return DSOFactory.userFromMap(map);
      case TRANSACTION_CLS:
        return DSOFactory.tXFromMap(map);
      default:
        return DSOFactory.detailedObjFromMap(map);
    }
  },

  detailedObjFromMap: (map: Record<string, any>): DetailedSafeObject => {
    try {
      const object = map["object"] ?? {};
      const parseIntSafe = (value: any, defaultValue: number): number => {
        const parsed = parseInt(String(value));
        return isNaN(parsed) ? defaultValue : parsed;
      };

      return DetailedSafeObjectUtils.create({
        isValid: false,
        process: ProcessUtils.fromMap(map["process"]),
        nestedObjects: DSOFactory.nestedObjectsFromMap({ map }),
        isDeleted: !!map["isDeleted"],
        applyFunction:
          object["applyFunction"] && object["applyFunction"] !== ""
            ? ApplyFunctionUtils.fromMap(object["applyFunction"])
            : null,
        originalObjectSIG: String(object["originalObjectSIG"] ?? ""),
        source: String(object["source"] ?? ""),
        destination: String(object["destination"] ?? ""),
        timestamp: parseIntSafe(object["timestamp"], -1),
        last_modified: parseIntSafe(object["last_modified"], -1),
        slot: parseIntSafe(object["slot"], -1),
        pending_objects: DSOFactory.parsePendingObjects(object),
        affected_by: DSOFactory.parsePendingObjects(object, {
          with_key: "affected_by",
        }),
        cls: String(object["class"] ?? ""),
        id: String(object["id"] ?? map["id"] ?? ""),
        parentGroup: String(object["parentGroup"] ?? ""),
        SIGNATURE: String(object["SIGNATURE"] ?? ""),
        comment: String(object["comment"] ?? ""),
        safes:
          object["safes"] && object["safes"] !== ""
            ? (object["safes"] as Array<Record<string, any>>)
            : null,
        trustors:
          object["trustors"] && object["trustors"] !== ""
            ? (object["trustors"] as Array<Record<string, any>>)
            : null,
        public_key: object["public_key"] ?? null,
        execute: object["execute"] ?? true,
      });
    } catch (e) {
      console.error(e);
      return DetailedSafeObjectUtils.empty();
    }
  },

  policyFromMap: (map: Record<string, any>): DetailedPolicy => {
    try {
      let object: Record<string, any>;
      if (map["object"] && typeof map["object"] === "object") {
        object = map["object"] as Record<string, any>;
      } else if (map["myPolicy"] && typeof map["myPolicy"] === "object") {
        object = map["myPolicy"] as Record<string, any>;
      } else {
        throw new Error("missing policy object in DetailedPolicy");
      }

      const applyOn =
        object["applyOn"] && object["applyOn"] !== ""
          ? (object["applyOn"] as any[]).map((e) => ApplyOnUtils.fromMap(e))
          : [];

      if (shouldIgnorePolicy(applyOn) && object["id"] !== "policy-allow-updateUserAddress") {
        return DetailedPolicyUtils.empty();
      }

      const conditions =
        object["conditions"] && object["conditions"] !== ""
          ? (object["conditions"] as any[])
              .map((e) => ConditionUtils.fromMap(e))
              .filter((c) => [...conditionsToParse].includes(c.cls))
          : [];

      const type = analyzePolicyType(applyOn);

      const comment =
        map["comment"] && map["comment"] !== ""
          ? String(map["comment"])
          : object["comment"] && object["comment"] !== ""
            ? String(object["comment"])
            : "";

      const parseIntSafe = (value: any, defaultValue: number): number => {
        const parsed = parseInt(String(value));
        return isNaN(parsed) ? defaultValue : parsed;
      };

      return DetailedPolicyUtils.create({
        last_modified: parseIntSafe(object["last_modified"], -1),
        affected_by: DSOFactory.parsePendingObjects(object, {
          with_key: "affected_by",
        }),
        timestamp: parseIntSafe(object["timestamp"], -1),
        slot: parseIntSafe(object["slot"], -1),
        source: String(object["source"] ?? ""),
        destination: String(object["destination"] ?? ""),
        applyFunction: object["applyFunction"]
          ? ApplyFunctionUtils.fromMap(object["applyFunction"])
          : null,
        originalObjectSIG: String(object["originalObjectSIG"] ?? ""),
        id: String(object["id"] ?? ""),
        name: String(object["name"] ?? ""),
        description: String(object["description"] ?? ""),
        parentGroup: String(object["parentGroup"] ?? ""),
        SIGNATURE: String(object["SIGNATURE"] ?? ""),
        comment,
        isValid: !!map["isValid"],
        isDeleted: !!map["isDeleted"],
        conditions,
        applyOn,
        type,
        nestedObjects:
          map["nestedObjects"] && map["nestedObjects"] !== ""
            ? (map["nestedObjects"] as any[]).map((e) => DSOFactory.fromMap(e))
            : [],
        process:
          map["process"] && map["process"] !== ""
            ? ProcessUtils.fromMap(map["process"])
            : ProcessUtils.empty(),
        pending_objects: DSOFactory.parsePendingObjects(object) as DetailedPolicy[],
        execute: object["execute"] ?? true,
      });
    } catch (e) {
      console.error(String(e));
      console.error(e); // Stack trace equivalent
      return DetailedPolicyUtils.empty();
    }
  },

  tXFromMap: (map: Record<string, any>): DetailedTransaction => {
    try {
      const object = map["object"] ?? {};
      const transfer = object["transfer"] ? (object["transfer"] as Record<string, any>[]) : [];
      const transferId = Array.isArray(transfer)
        ? (transfer.find((t: Record<string, string | undefined>) => t.key === "transfer_id")
            ?.value ?? "")
        : "";
      const nestedObjects = (map["nestedObjects"] || []).map((item: any) => item); // Placeholder
      const assetFromMap = object["asset"] && object["asset"] !== "" ? object["asset"] : "Error";
      const process = map["process"] || {
        currentPhase: CurrentPhaseUtils.create({
          index: BasicProcessPhase.NON_EXISTS,
          name: "",
          description: "",
          data: [],
          errors: [],
        }),
        phases: [],
      };
      const transactionMessage =
        object["message"] && object["message"] !== ""
          ? object["message"]
          : TransactionMessageUtils.empty();
      const parseIntSafe = (value: any, defaultValue: number): number => {
        const parsed = parseInt(String(value));
        return isNaN(parsed) ? defaultValue : parsed;
      };

      return DetailedTransactionUtils.create({
        fee_rate: parseIntSafe(object["fee_rate"], 0),
        asset: assetFromMap,
        amount:
          typeof object["amount"] === "number"
            ? object["amount"]
            : parseIntSafe(object["amount"], 0),
        address: String(object["address"] ?? ""),
        transactionMessage,
        layer2data: object["layer2data"],
        isDeleted: !!map["isDeleted"],
        isValid: !!map["isValid"],
        applyFunction:
          object["applyFunction"] && object["applyFunction"] !== ""
            ? ApplyFunctionUtils.fromMap(object["applyFunction"])
            : null,
        originalObjectSIG: String(object["originalObjectSIG"] ?? ""),
        id: String(map["id"] ?? object["id"] ?? ""),
        parentGroup: String(object["parentGroup"] ?? ""),
        SIGNATURE: String(object["SIGNATURE"] ?? ""),
        comment: String(object["comment"] ?? ""),
        process,
        nestedObjects,
        source: String(object["source"] ?? ""),
        destination: String(object["destination"] ?? ""),
        slot: parseIntSafe(object["slot"], -1),
        timestamp: parseIntSafe(object["timestamp"], -1),
        last_modified: parseIntSafe(object["last_modified"], -1),
        pending_objects: (object["pending_objects"] || []).map((item: any) => item), // Placeholder
        affected_by: (object["affected_by"] || []).map((item: any) => item), // Placeholder
        execute: object["execute"] ?? true,
        transferId: transferId,
      });
    } catch (e) {
      console.error(e);
      return DetailedTransactionUtils.empty();
    }
  },

  userFromMap: (map: Record<string, any>): DetailedUser => {
    try {
      const object = map["object"] ?? {};
      const nestedObjects =
        map["nestedObjects"] && map["nestedObjects"] !== ""
          ? (map["nestedObjects"] as any[]).map((e) => DSOFactory.fromMap(e))
          : [];

      const link =
        map["Link"] || map["link"]
          ? LinkUtils.fromMap((map["Link"] || map["link"]) as Record<string, any>)
          : LinkUtils.empty();

      const profile =
        object["profile"] && object["profile"] !== ""
          ? (object["profile"] as any[]).map((p) => ParamUtils.fromMap(p))
          : [];

      const publicKeyRaw = object["public_key"];
      let public_key: string;
      let public_keys: string[];
      if (typeof publicKeyRaw === "string") {
        public_key = publicKeyRaw;
        public_keys = [publicKeyRaw];
      } else if (Array.isArray(publicKeyRaw)) {
        public_key =
          (publicKeyRaw as string[]).length > 0 ? publicKeyRaw[publicKeyRaw.length - 1] : link.id;
        public_keys = publicKeyRaw as string[];
      } else {
        public_key = link.id;
        public_keys = [link.id];
      }

      const dependenciesRaw = object["dependencies"];
      const dependencies =
        dependenciesRaw && Array.isArray(dependenciesRaw) && dependenciesRaw.length > 0
          ? JSON.stringify(dependenciesRaw[0])
          : String(dependenciesRaw ?? "");

      const parseIntSafe = (value: any, defaultValue: number): number => {
        const parsed = parseInt(String(value));
        return isNaN(parsed) ? defaultValue : parsed;
      };

      const u = DetailedUserUtils.create({
        isDeleted: !!map["isDeleted"],
        isValid: !!map["isValid"],
        applyFunction: object["applyFunction"]
          ? ApplyFunctionUtils.fromMap(object["applyFunction"])
          : null,
        originalObjectSIG: String(object["originalObjectSIG"] ?? ""),
        link_Path: String(object["link_Path"] ?? ""),
        public_key,
        public_keys,
        dependencies,
        link,
        profile,
        id: String(object["id"] ?? ""),
        parentGroup: String(object["parentGroup"] ?? ""),
        SIGNATURE: String(object["SIGNATURE"] ?? ""),
        comment: String(object["comment"] ?? ""),
        process:
          map["process"] && map["process"] !== ""
            ? ProcessUtils.fromMap(map["process"])
            : ProcessUtils.empty(),
        nestedObjects,
        source: String(object["source"] ?? ""),
        destination: String(object["destination"] ?? ""),
        slot: parseIntSafe(object["slot"], -1),
        timestamp: parseIntSafe(object["timestamp"], -1),
        last_modified: parseIntSafe(object["last_modified"], -1),
        pending_objects: (object["pending_objects"] || []).map((item: DetailedUser) =>
          DSOFactory.fromMap(item)
        ), // Placeholder
        affected_by: (object["affected_by"] || []).map((item: DetailedUser) => item), // Placeholder
        execute: object["execute"] ?? true,
      });
      return u;
    } catch (e) {
      console.error(e);
      return DetailedUserUtils.empty();
    }
  },

  nestedObjectsFromMap: ({
    map,
    key = "nestedObjects",
  }: {
    map: Record<string, any>;
    key?: string;
  }): DetailedSafeObject[] => {
    // This method extracts nested objects from the provided map and returns them as an array of DetailedSafeObject.
    try {
      return map[key] && map[key] !== ""
        ? (map[key] as any[])
            .map((e) => DSOFactory.fromMap(e))
            .filter(
              (n) =>
                [GROUP_CLS, POLICY_CLS, USER_CLS, TRANSACTION_CLS, PROFILE_CLS].includes(n.cls) &&
                n.id !== ""
            )
        : [];
    } catch (e) {
      console.error(e);
      return [];
    }
  },

  parsePendingObjects(
    object: Record<string, any>,
    { with_key = "pending_objects" }: { with_key?: string } = {}
  ): DetailedSafeObject[] {
    if (
      !object[with_key] ||
      object[with_key] === "" ||
      (object[with_key] as any[]).every((e) => typeof e === "string")
    ) {
      return [];
    }
    return (object[with_key] as any[]).map((po) => {
      if (po["id"] === object["id"]) {
        const pendingObjObject = po["object"] as Record<string, any>;
        if (!pendingObjObject["last_modified"] || pendingObjObject["last_modified"] === -1) {
          pendingObjObject["last_modified"] = pendingObjObject["timestamp"] ?? 0;
          po["object"] = { ...pendingObjObject };
        }
      }
      return DSOFactory.fromMap(po);
    });
  },

  // This method creates a DetailedGroup instance from the provided map.
  groupFromMap: (map: Record<string, any>): DetailedGroup => {
    try {
      const object = map["object"] ?? {};
      const pending_objects = DSOFactory.parsePendingObjects(object);
      const nestedObjects = DSOFactory._groupsNestedObjects(map);

      const parseIntSafe = (value: any, defaultValue: number): number => {
        const parsed = parseInt(String(value));
        return isNaN(parsed) ? defaultValue : parsed;
      };

      return DetailedGroupUtils.create({
        last_modified: parseIntSafe(object["last_modified"], -1),
        affected_by: DSOFactory.parsePendingObjects(object, { with_key: "affected_by" }),
        timestamp: parseIntSafe(object["timestamp"], -1),
        slot: parseIntSafe(object["slot"], -1),
        source: String(object["source"] ?? ""),
        destination: String(object["destination"] ?? ""),
        applyFunction:
          object["applyFunction"] && object["applyFunction"] !== ""
            ? ApplyFunctionUtils.fromMap(object["applyFunction"])
            : null,
        originalObjectSIG: String(object["originalObjectSIG"] ?? ""),
        isDeleted: !!map["isDeleted"],
        id: String(object["id"] ?? ""),
        name: String(object["name"] ?? ""),
        parentGroup: String(object["parentGroup"] ?? ""),
        SIGNATURE: String(object["SIGNATURE"] ?? ""),
        groupOwner: object["groupOwner"] ? (object["groupOwner"] as string[]) : [],
        comment: String(object["comment"] ?? ""),
        isValid: !!map["isValid"],
        nestedObjects,
        policies:
          map["nestedPolicies"] && map["nestedPolicies"] !== ""
            ? (map["nestedPolicies"] as any[])
                .map((e) => DSOFactory.policyFromMap(e))
                .filter((p) => p.isValid && p.id !== "")
            : [],
        users:
          map["nestedUsers"] && map["nestedUsers"] !== ""
            ? (map["nestedUsers"] as any[])
                .map((e) => DSOFactory.userFromMap(e))
                .filter((u) => u.isValid)
            : [],

        process:
          map["process"] && map["process"] !== ""
            ? ProcessUtils.fromMap(map["process"])
            : ProcessUtils.empty(),
        pending_objects,
        execute: object["execute"] ?? true,
      });
    } catch (e) {
      console.error(e);
      return DetailedGroupUtils.empty();
    }
  },

  _groupsNestedObjects: (map: Record<string, any>): DetailedSafeObject[] => {
    // This method extracts nested objects from the provided map and returns them as an array of DetailedSafeObject.
    try {
      const nestedObjects = DSOFactory.nestedObjectsFromMap({ map: map });
      const pending_nestedObjects: DetailedSafeObject[] = [];
      for (let i = 0; i < nestedObjects.length; i++) {
        const n = nestedObjects[i];
        if (DetailedPolicyUtils.isDetailedPolicy(n)) {
          for (let j = 0; j < n.pending_objects.length; j++) {
            const p = n.pending_objects[j] as DetailedPolicy;
            if (n.pending_objects[j].applyFunction?.functionName === "deleteObject") {
              n.pending_objects[j] = DetailedPolicyUtils.copyWith(p as DetailedPolicy, {
                applyOn: n.applyOn,
                type: n.type as PolicyType,
                name: n.name as string,
                description: n.description as string,
              });
            } else {
              n.pending_objects[j] = p as DetailedPolicy;
            }
          }
          pending_nestedObjects.push(...n.pending_objects);
        } else {
          for (let j = 0; j < n.pending_objects.length; j++) {
            const p = n.pending_objects[j];
            n.pending_objects[j] = DetailedSafeObjectUtils.copyWith(p as DetailedSafeObject, {
              safes: n.safes,
              trustors: n.trustors,
              public_key: n.public_key,
            });
          }
          pending_nestedObjects.push(...n.pending_objects);
        }
      }
      nestedObjects.push(...pending_nestedObjects);
      return nestedObjects;
    } catch (e) {
      console.error(e);
      return [];
    }
  },
};
